import { useEffect, useMemo, useRef } from "react"
import { useLocation, useMatches, useNavigate, useParams, useSearchParams } from "react-router"

/**
 * `next/router` shim — Vite/React Router implementation (NEX-248 Phase C).
 *
 * Imported DIRECTLY by the navigation adapter (`@/common/navigation`), which
 * exposes a framework-neutral surface to the app so no consumer is aware of
 * which implementation backs it. There is no `next/*` resolve alias any more —
 * `vite.config.ts`, `vitest.config.ts` and `tsconfig.json` each declare exactly
 * two aliases (`@` and `@clerk/nextjs`), and no file in `src/` imports
 * `next/router` or `next/navigation`.
 *
 * Surface implemented = exactly what the adapter consumes:
 * `push/replace/back/reload/query/pathname/asPath/isReady` and `events`
 * (routeChangeStart only).
 */

type NavQuery = Record<string, string | string[] | undefined>
type RouteHandler = (url: string) => void
type UrlObject = { pathname?: string; query?: NavQuery }
type Url = string | UrlObject

interface NextRouterShim {
  push(url: Url, as?: Url, options?: { scroll?: boolean; shallow?: boolean }): Promise<boolean>
  replace(url: Url, as?: Url, options?: { scroll?: boolean; shallow?: boolean }): Promise<boolean>
  back(): void
  reload(): void
  query: NavQuery
  pathname: string
  asPath: string
  isReady: boolean
  events: typeof routerEvents
}

// Module-level emitter. React Router has no global route-event bus, so the
// emitter is driven by `useRouteChangeEmitter` (mounted once in main.tsx),
// which fires on every location change — covering programmatic nav, <Link>
// clicks, and browser back/forward alike.
const listeners = new Map<string, Set<RouteHandler>>()

export const routerEvents = {
  on(event: string, handler: RouteHandler): void {
    const set = listeners.get(event) ?? new Set<RouteHandler>()
    set.add(handler)
    listeners.set(event, set)
  },
  off(event: string, handler: RouteHandler): void {
    listeners.get(event)?.delete(handler)
  },
  emit(event: string, url: string): void {
    listeners.get(event)?.forEach((handler) => handler(url))
  }
}

/**
 * `next/router` default export — the Router singleton. The app uses the named
 * `useRouter` hook; the singleton exists for non-hook consumers that do
 * `import Router from "next/router"` and read `route`/`pathname`/`asPath` or
 * subscribe to `Router.events`. Those fields are kept current by
 * `useRouteChangeEmitter`, and `events` is backed by the module emitter below.
 */
const singletonRouter = {
  route: "/",
  pathname: "/",
  asPath: "/",
  query: {} as NavQuery,
  isReady: true,
  events: routerEvents,
  push: (url: Url) => {
    window.location.assign(toHref(url, window.location.pathname))
    return Promise.resolve(true)
  },
  replace: (url: Url) => {
    window.location.replace(toHref(url, window.location.pathname))
    return Promise.resolve(true)
  },
  back: () => window.history.back(),
  reload: () => window.location.reload(),
  prefetch: () => Promise.resolve(),
  beforePopState: () => undefined
}

export default singletonRouter

/** Mount once inside the Router (main.tsx) to drive routeChangeStart events. */
export function useRouteChangeEmitter(): void {
  const location = useLocation()
  const url = `${location.pathname}${location.search}${location.hash}`
  // Keep the next/router singleton current from first paint — not just after the
  // first navigation — so non-hook consumers (e.g. Sentry) that read it on load
  // see the real location, not the "/" placeholder. Idempotent mirror of router state.
  singletonRouter.pathname = location.pathname
  singletonRouter.route = location.pathname
  singletonRouter.asPath = url
  const previous = useRef(url)
  useEffect(() => {
    if (previous.current !== url) {
      routerEvents.emit("routeChangeStart", url)
      previous.current = url
    }
  }, [url])
}

/**
 * Serialize a `NavTarget` to a URL. `skipKeys` are the current route's dynamic
 * params (path params + the splat name): callers commonly spread the whole
 * `router.query` into a push target (`{ ...router.query, tab }`), but those
 * dynamic-segment keys are already encoded in `pathname` — emitting them into
 * the search string would add stray params and, for the splat array, collide
 * with the value re-published from the path. Dropping them mirrors how Next
 * consumes dynamic-segment keys into the path. Array values append (Next's
 * repeated-key form), never truncate.
 */
function toHref(url: Url, currentPathname: string, skipKeys?: ReadonlySet<string>): string {
  if (typeof url === "string") return url
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(url.query ?? {})) {
    if (value === undefined || skipKeys?.has(key)) continue
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, item)
    } else {
      search.set(key, value)
    }
  }
  const qs = search.toString()
  return `${url.pathname ?? currentPathname}${qs ? `?${qs}` : ""}`
}

/**
 * Absolute-URL test, byte-identical to React Router's own `ABSOLUTE_URL_REGEX`:
 * a scheme, or the protocol-relative `//`.
 */
const ABSOLUTE_URL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i

/** Who carries this navigation: the browser (leaving the app) or React Router. */
type NavigationTarget =
  /** A full-page navigation. `href` is handed to `location.assign`/`replace`. */
  | { readonly kind: "browser"; readonly href: string }
  /** An in-app transition. `to` is always a path, never an absolute URL. */
  | { readonly kind: "router"; readonly to: string }

/**
 * Decide who carries a navigation — the one gate this shim was missing.
 *
 * 🚨 THIS IS `next/router`'s `isLocalURL` GATE, RESTORED. It is the one part of
 * the Next router the Vite cutover did not carry across, and its absence is
 * silent by construction.
 *
 * `useNavigate()` has NO absolute-URL branch in react-router 7.9.6 — the only
 * three in that build are `<Link>`, `<Form action>` and `encodeLocation`, and
 * `encodeLocation` is the one the navigate path reaches. It RECOGNISES an
 * absolute URL (`ABSOLUTE_URL_REGEX.test(href) ? new URL(href) : …`) and then
 * returns only `{ pathname, search, hash }`, so the ORIGIN IS DISCARDED and
 * `pushState` puts the provider's path on OURS. Nothing throws: the
 * `SecurityError` a cross-origin `pushState` would raise never fires, because
 * by then nothing about the target is cross-origin. Measured live on staging
 * 2026-09-19 — every built-in-OAuth "Connect" landed on
 * `https://staging.gpt.nexus/api/oauth/authorize?…`, our origin carrying the
 * provider's path, with no network request at all.
 *
 * 🔴 A SAME-ORIGIN ABSOLUTE URL IS NORMALISED TO A PATH HERE RATHER THAN BEING
 * PASSED THROUGH, and that is not tidying. Only the BROWSER history strips an
 * origin; MEMORY history does not — it warns `relative pathnames are not
 * supported in memory history` and keeps `pathname` as the whole URL, so the
 * two histories disagree about the same input. Passing it through would leave
 * this shim depending on the precise react-router internal that caused the bug
 * above. Handing React Router a path, always, makes the two agree by
 * construction. Found by `router.external-navigation.test.tsx`, which reddened
 * on the first draft of this function.
 *
 * ⚠️ DELIBERATELY NARROWER THAN NEXT'S `isLocalURL`, which hard-navigates every
 * non-local URL, `javascript:` included. Only `http:` and `https:` leave the
 * document here: today `push("javascript:…")` resolves to a harmless in-app
 * path, and reproducing Next exactly would mint a
 * `location.assign("javascript:…")` execution sink that did not previously
 * exist. Nothing in the app pushes another scheme, so this costs no reachable
 * target.
 *
 * The browser branch hands back the CALLER's own string, not `URL`'s
 * normalisation, so the exact href the backend built is the exact href the
 * browser is given.
 */
function navigationTargetOf(href: string): NavigationTarget {
  if (!ABSOLUTE_URL.test(href)) return { kind: "router", to: href }

  let target: URL
  try {
    target = new URL(href, window.location.origin)
  } catch {
    // Unparseable as a URL — leave it to React Router, exactly as before.
    return { kind: "router", to: href }
  }

  if (target.origin === window.location.origin) {
    return { kind: "router", to: `${target.pathname}${target.search}${target.hash}` }
  }

  return target.protocol === "http:" || target.protocol === "https:"
    ? { kind: "browser", href }
    : { kind: "router", to: href }
}

/**
 * The catch-all routes carry `handle: { splat: "<next-name>" }` (see routes.tsx).
 * React Router exposes the catch-all remainder only under the `*` param, so we
 * read the matched route's handle to re-publish it under Next's name as a
 * string[] — matching `[[...slug]]` -> query.slug, `[...path]` -> query.path, etc.
 */
function splatNameOf(handle: unknown): string | undefined {
  if (typeof handle !== "object" || handle === null || !("splat" in handle)) return undefined
  const value = (handle as { splat: unknown }).splat
  return typeof value === "string" ? value : undefined
}

export function useRouter(): NextRouterShim {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const matches = useMatches()

  const pathname = location.pathname
  const asPath = `${location.pathname}${location.search}${location.hash}`

  const splatName = splatNameOf(matches[matches.length - 1]?.handle)

  const query = useMemo<NavQuery>(() => {
    const merged: NavQuery = {}
    for (const [key, value] of Object.entries(params)) {
      // The raw `*` splat is React-Router-only; it is re-published below under
      // the page's Next catch-all name, so it is never exposed verbatim.
      if (key === "*" || value === undefined) continue
      merged[key] = value
    }
    if (splatName) {
      const segments = (params["*"] ?? "").split("/").filter(Boolean)
      // Optional catch-all at its base path yields no segments -> undefined,
      // exactly as Next leaves `query.<name>` undefined there.
      if (segments.length > 0) merged[splatName] = segments
    }
    for (const key of searchParams.keys()) {
      const all = searchParams.getAll(key)
      merged[key] = all.length > 1 ? all : (all[0] ?? "")
    }
    return merged
  }, [params, searchParams, splatName])

  // Dynamic-segment keys for the current route — dropped from any spread query
  // (see toHref). Path params minus the RR-only `*`, plus the splat's Next name.
  const routeParamKeys = useMemo(() => {
    const keys = new Set(Object.keys(params).filter((key) => key !== "*"))
    if (splatName) keys.add(splatName)
    return keys
  }, [params, splatName])

  // navigate identity is stable across renders; refs keep the callbacks stable
  // without reading stale path/param state.
  const pathRef = useRef(pathname)
  pathRef.current = pathname
  const paramKeysRef = useRef(routeParamKeys)
  paramKeysRef.current = routeParamKeys

  return useMemo<NextRouterShim>(() => {
    // Both halves route through `navigationTargetOf` first: a cross-origin
    // target leaves the document through the BROWSER, because React Router
    // would strip its origin and land the provider's path on ours. See that
    // function for the mechanism and for why same-origin is normalised.
    const push: NextRouterShim["push"] = (url) => {
      const target = navigationTargetOf(toHref(url, pathRef.current, paramKeysRef.current))
      if (target.kind === "router") navigate(target.to)
      else window.location.assign(target.href)
      return Promise.resolve(true)
    }
    const replace: NextRouterShim["replace"] = (url) => {
      const target = navigationTargetOf(toHref(url, pathRef.current, paramKeysRef.current))
      // `location.replace`, not `assign`: a router `replace` must not leave a
      // history entry, exactly as the React Router branch beside it does not.
      if (target.kind === "router") navigate(target.to, { replace: true })
      else window.location.replace(target.href)
      return Promise.resolve(true)
    }
    return {
      push,
      replace,
      back: () => navigate(-1),
      reload: () => window.location.reload(),
      query,
      pathname,
      asPath,
      isReady: true,
      events: routerEvents
    }
  }, [navigate, query, pathname, asPath])
}
